/* sm-sale-context.js
   Purpose:
   - Detect private-sale context from URL (?sale=holiday&sp=50)
   - Persist it ONLY for the current tab/session (sessionStorage)
   - Sync it into cart.attributes (reliable for Liquid on PDP + section renders)
   - Optionally decorate internal links + patch section-render requests (kept, but no longer required for Liquid)
*/

(function () {
  "use strict";

  var KEY = "sm_sale_context_v2";

  // ---------------------------
  // Helpers
  // ---------------------------

  function getParamsFromSearch(search) {
    try {
      var qs = new URLSearchParams(search || "");
      var sale = (qs.get("sale") || "").toLowerCase();
      var spRaw = qs.get("sp");
      var sp = spRaw == null ? null : parseInt(spRaw, 10);

      if (sale === "holiday" && Number.isFinite(sp) && sp > 0) {
        return { sale: sale, sp: sp };
      }
    } catch (e) {}
    return null;
  }

  function load() {
    try {
      var raw = sessionStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function save(ctx) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(ctx));
    } catch (e) {}
  }

  function clear() {
    try {
      sessionStorage.removeItem(KEY);
    } catch (e) {}
  }

  function currentCtx() {
    return getParamsFromSearch(window.location.search) || load();
  }

  function shouldSkipLink(href) {
    if (!href) return true;
    if (href.indexOf("#") === 0) return true;
    if (href.indexOf("mailto:") === 0) return true;
    if (href.indexOf("tel:") === 0) return true;
    if (href.indexOf("/account") === 0) return true;
    if (href.indexOf("/cart") === 0) return true;
    if (href.indexOf("/checkout") === 0) return true;
    return false;
  }

  // ---------------------------
  // Cart attributes bridge (RELIABLE)
  // ---------------------------

  var lastSentSig = null;

  function setCartAttrs(attrs) {
    try {
      var sig = JSON.stringify(attrs || {});
      if (sig === lastSentSig) return;
      lastSentSig = sig;

      // Use /cart/update.js — works without needing cart token access
      fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ attributes: attrs }),
        credentials: "same-origin",
      }).catch(function () {});
    } catch (e) {}
  }

  function syncCtxToCart(ctx) {
    if (ctx && ctx.sale === "holiday" && Number.isFinite(ctx.sp) && ctx.sp > 0) {
      setCartAttrs({
        sm_sale: "holiday",
        sm_sp: String(ctx.sp),
      });
    } else {
      // Clear attributes so it doesn't "stick" outside sale context
      setCartAttrs({
        sm_sale: "",
        sm_sp: "",
      });
    }
  }

  // ---------------------------
  // Optional: decorate internal links with sale/sp
  // (useful for analytics + keeping URL consistent, but Liquid won't depend on it anymore)
  // ---------------------------

  function decorateInternalLinks(ctx) {
    if (!ctx) return;

    var links = document.querySelectorAll('a[href^="/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var href = a.getAttribute("href");
      if (shouldSkipLink(href)) continue;

      // Don't overwrite existing sale params
      if (href.indexOf("sale=") !== -1 || href.indexOf("sp=") !== -1) continue;

      try {
        var url = new URL(href, window.location.origin);
        url.searchParams.set("sale", ctx.sale);
        url.searchParams.set("sp", String(ctx.sp));
        a.setAttribute("href", url.pathname + "?" + url.searchParams.toString() + url.hash);
      } catch (e) {}
    }
  }

  // Re-run decoration for dynamically injected links
  function observeDomForLinks(ctx) {
    if (!ctx || !window.MutationObserver) return;

    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        decorateInternalLinks(ctx);
      }, 200);
    });

    try {
      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  // ---------------------------
  // Optional: append ctx to section-render requests (kept from your version)
  // ---------------------------

  function appendCtxToSectionRequest(inputUrl, ctx) {
    try {
      var url = new URL(inputUrl, window.location.origin);

      // only same-origin
      if (url.origin !== window.location.origin) return inputUrl;

      // only section renders
      if (!url.searchParams.has("section_id")) return inputUrl;

      // don't pollute if already present
      if (url.searchParams.has("sale") || url.searchParams.has("sp")) return inputUrl;

      url.searchParams.set("sale", ctx.sale);
      url.searchParams.set("sp", String(ctx.sp));
      return url.toString();
    } catch (e) {
      return inputUrl;
    }
  }

  function patchFetch(ctx) {
    if (!window.fetch) return;
    var originalFetch = window.fetch;

    window.fetch = function (input, init) {
      try {
        if (typeof input === "string") {
          input = appendCtxToSectionRequest(input, ctx);
        } else if (input && input.url) {
          var newUrl = appendCtxToSectionRequest(input.url, ctx);
          if (newUrl !== input.url) {
            input = new Request(newUrl, input);
          }
        }
      } catch (e) {}
      return originalFetch.call(this, input, init);
    };
  }

  function patchXHR(ctx) {
    if (!window.XMLHttpRequest) return;
    var open = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        url = appendCtxToSectionRequest(url, ctx);
      } catch (e) {}

      var args = Array.prototype.slice.call(arguments);
      args[1] = url;
      return open.apply(this, args);
    };
  }

  // ---------------------------
  // Boot
  // ---------------------------

  // 1) If URL includes ctx, store it for this tab
  var fromUrl = getParamsFromSearch(window.location.search);
  if (fromUrl) {
    save(fromUrl);
  }

  // 2) Resolve best ctx we have
  var ctx = currentCtx();

  // 3) Sync to cart attributes (the key fix)
  syncCtxToCart(ctx);

  // 4) If we have ctx, keep URL + section renders consistent (optional)
  if (ctx) {
    patchFetch(ctx);
    patchXHR(ctx);
    decorateInternalLinks(ctx);
    observeDomForLinks(ctx);
  }

  // 5) Optional hard-clear rule:
  // If you want the context to die automatically whenever user lands on a page
  // without ?sale/sp AND it's not your private landing page, uncomment below.
  //
  // var isPrivateLanding = (window.location.pathname === "/pages/holiday-sales");
  // if (!fromUrl && !isPrivateLanding) {
  //   clear();
  //   syncCtxToCart(null);
  // }

})();
